/**
=========================================================
* BAXI NOVA - Servicio Técnico Oficial
=========================================================

* Copyright 2025 BAXI NOVA
* Servicio Técnico Oficial de Calderas y Calefacción

=========================================================
*/

import { useEffect, Suspense, lazy, useState } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

// Import custom fonts
import "assets/fonts/loede-font.css";

// WhatsApp floating button
import WhatsAppButton from "components/WhatsAppButton";

// Custom hook for delayed loading
const useDelayedSuspense = (delay = 3000) => {
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  return showLoader;
};

// Enhanced PageLoader with minimum display time for route transitions
const EnhancedPageLoader = () => {
  const showLoader = useDelayedSuspense(300); // 300ms minimum for smooth transitions

  if (!showLoader) return null;

  return <PageLoader />;
};

// BAXI NOVA themes
import theme from "assets/theme";

// Lazy load components for better performance
const Presentation = lazy(() => import("layouts/pages/presentation"));
const Services = lazy(() => import("pages/LandingPages/Services"));
const AboutUs = lazy(() => import("pages/LandingPages/AboutUs"));
const Coverage = lazy(() => import("pages/LandingPages/Coverage"));
const ContactUs = lazy(() => import("pages/LandingPages/ContactUs"));

// Loading component with smooth animation
const PageLoader = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    sx={{
      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.79), rgba(255, 255, 255, 0.09))",
      animation: "fadeIn 1.5s ease-in-out",
      "@keyframes fadeIn": {
        "0%": { opacity: 0 },
        "100%": { opacity: 1 },
      },
    }}
  >
    <Box
      sx={{
        position: "relative",
        width: 95,
        height: 95,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          border: "4px solid transparent",
          borderTop: "4px solid #1976d2",
          animation: "spin 2s linear infinite",
        },
        "@keyframes spin": {
          "0%": { transform: "rotate(0deg)" },
          "100%": { transform: "rotate(360deg)" },
        },
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          backgroundColor: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          zIndex: 1,
        }}
      >
        <Box
          component="img"
          src="/static/media/LOGO%20PNG%20BAXI.c53c83cdb52ff0146183.png"
          alt="Baxi Logo"
          sx={{
            width: 70,
            height: 70,
            objectFit: "contain",
          }}
        />
      </Box>
    </Box>
    <Box
      sx={{
        color: "white",
        opacity: 0.8,
        animation: "fadeInOut 4s ease-in-out infinite",
        "@keyframes fadeInOut": {
          "0%, 100%": { opacity: 0.3 },
          "50%": { opacity: 0.8 },
        },
      }}
    >
      Cargando...
    </Box>
  </Box>
);

// Updated routes with lazy loading
const lazyRoutes = [
  {
    name: "Servicios",
    route: "/servicios",
    component: Services,
  },
  {
    name: "Sobre Nosotros",
    route: "/sobre-nosotros",
    component: AboutUs,
  },
  {
    name: "Cobertura",
    route: "/cobertura",
    component: Coverage,
  },
  {
    name: "Contacto",
    route: "/contacto",
    component: ContactUs,
  },
];

export default function App() {
  const { pathname } = useLocation();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Show initial loader for a minimum time
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1200); // 1.2 seconds initial load

    return () => clearTimeout(timer);
  }, []);

  // Setting page scroll to 0 when changing the route with smooth transition
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    // Small delay to ensure smooth transition
    const timer = setTimeout(scrollToTop, 100);
    return () => clearTimeout(timer);
  }, [pathname]);

  // Preload critical routes on app start
  useEffect(() => {
    // Preload services page (most visited)
    const preloadServices = () => {
      import("pages/LandingPages/Services");
    };

    // Preload contact page (important for conversions)
    const preloadContact = () => {
      import("pages/LandingPages/ContactUs");
    };

    // Delay preloading to not affect initial load
    setTimeout(() => {
      preloadServices();
      setTimeout(preloadContact, 2000);
    }, 3000);
  }, []);

  // Show initial loader on app start/reload
  if (isInitialLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PageLoader />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={<EnhancedPageLoader />}>
        <Routes>
          {/* Lazy loaded routes */}
          {lazyRoutes.map((route) => (
            <Route key={route.route} path={route.route} element={<route.component />} />
          ))}

          {/* Main presentation page */}
          <Route path="/" element={<Presentation />} />
          <Route path="/presentation" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
      <WhatsAppButton />
    </ThemeProvider>
  );
}
